import { PageLoader, useMinLoading } from "@/components/Preloader";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { TrendingUp, TrendingDown, Activity, ArrowUpRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from "recharts";
import api from "@/lib/api";
import { Article } from "@/lib/types";
import { ArticleCard } from "@/components/ArticleCard";
import { WatchButton } from "@/components/WatchButton";
import { EntityLogo } from "@/components/entity/EntityLink";
import { entityTrack, useTokenProtocolBridge } from "@/lib/entities";
import { Seo } from "@/components/Seo";

const RANGES = [
  { key: "24h", label: "24H" },
  { key: "7d", label: "7D" },
  { key: "30d", label: "30D" },
];

const fmtPrice = (p: number) =>
  p >= 1000 ? p.toLocaleString(undefined, { maximumFractionDigits: 0 })
    : p.toLocaleString(undefined, { maximumFractionDigits: p >= 1 ? 2 : 4 });

export default function CoinPage() {
  const { symbol } = useParams();
  const [range, setRange] = useState("24h");
  const { protocol: assocProtocol, route: assocRoute } = useTokenProtocolBridge(symbol);

  const { data: coin, isLoading, isError } = useQuery({
    queryKey: ["coin", symbol, range],
    queryFn: async () => (await api.get(`/markets/${symbol}?range=${range}`)).data,
    refetchInterval: 60000,
  });
  const { data: news } = useQuery({
    queryKey: ["coin-news", symbol],
    queryFn: async () => (await api.get(`/markets/${symbol}/news`)).data,
  });
  const showLoader = useMinLoading(isLoading, 700, symbol);

  if (isError) {
    return <div className="max-w-4xl mx-auto px-4 py-24 text-center">
      <h1 className="font-heading text-2xl text-white mb-3">Coin not found</h1>
      <Link to="/" className="text-[#00E5FF] hover:underline">Back to homepage</Link>
    </div>;
  }

  const up = (coin?.change_24h ?? 0) >= 0;
  const accent = up ? "#22C55E" : "#EF4444";
  const chart = (coin?.chart || []).map((p: any) => ({
    ...p,
    label: range === "24h"
      ? new Date(p.t * 1000).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      : new Date(p.t * 1000).toLocaleDateString([], { month: "short", day: "numeric" }),
  }));

  return (
    <div data-testid="coin-page" className="max-w-7xl mx-auto px-4 sm:px-6 py-10">
      <Seo title={`${(symbol || "").toUpperCase()} Price, Chart & News`}
        description={`Live ${(symbol || "").toUpperCase()} price, charts and the latest news — tracked in real time on NewsLayer.`}
        path={`/coin/${symbol}`} />
      {showLoader || !coin ? (
        <PageLoader />
      ) : (
        <>
          <div className="flex flex-wrap items-end justify-between gap-4">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#00E5FF] mb-2 flex items-center gap-1.5">
                <Activity className="h-3.5 w-3.5" /> Live Market
              </p>
              <h1 className="font-heading font-black text-white text-3xl sm:text-5xl" data-testid="coin-name">
                {coin.name} <span className="text-gray-500 text-xl sm:text-3xl font-bold">{coin.symbol}</span>
              </h1>
              <div className="flex items-baseline gap-3 mt-3">
                <span className="font-mono text-3xl sm:text-4xl font-bold text-white" data-testid="coin-price">
                  ${fmtPrice(coin.price)}
                </span>
                <span data-testid="coin-change"
                  className={`flex items-center font-mono text-sm font-semibold ${up ? "text-green-400" : "text-red-400"}`}>
                  {up ? <TrendingUp className="h-4 w-4 mr-1" /> : <TrendingDown className="h-4 w-4 mr-1" />}
                  {up ? "+" : ""}{coin.change_24h}% · 24h
                </span>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <WatchButton symbol={(symbol || "").toUpperCase()} />
              <div className="flex gap-1.5" data-testid="coin-range-selector">
                {RANGES.map((r) => (
                  <button key={r.key} onClick={() => setRange(r.key)} data-testid={`coin-range-${r.key}`}
                    className={`font-mono text-xs px-4 py-2 rounded-md border transition-colors ${
                      range === r.key
                        ? "bg-[#00E5FF]/15 border-[#00E5FF]/50 text-[#00E5FF]"
                        : "border-white/10 text-gray-400 hover:text-white hover:border-white/30"}`}>
                    {r.label}
                  </button>
                ))}
              </div>
            </div>
          </div>

          <div className="mt-8 bg-[#121212] border border-white/10 rounded-xl p-4 sm:p-6" data-testid="coin-chart">
            <ResponsiveContainer width="100%" height={320}>
              <AreaChart data={chart} margin={{ top: 8, right: 8, bottom: 0, left: 8 }}>
                <defs>
                  <linearGradient id="coinFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={accent} stopOpacity={0.3} />
                    <stop offset="100%" stopColor={accent} stopOpacity={0} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="label" tick={{ fill: "#6b7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={false} tickLine={false} minTickGap={48} />
                <YAxis domain={["auto", "auto"]} orientation="right" width={70}
                  tick={{ fill: "#6b7280", fontSize: 10, fontFamily: "JetBrains Mono" }}
                  axisLine={false} tickLine={false}
                  tickFormatter={(v: number) => `$${fmtPrice(v)}`} />
                <Tooltip
                  contentStyle={{ background: "#0a0a0a", border: "1px solid rgba(255,255,255,0.15)", borderRadius: 8, fontFamily: "JetBrains Mono", fontSize: 12 }}
                  labelStyle={{ color: "#9ca3af" }}
                  formatter={(v: number) => [`$${fmtPrice(v)}`, coin.symbol]} />
                <Area type="monotone" dataKey="price" stroke={accent} strokeWidth={2}
                  fill="url(#coinFill)" dot={false} isAnimationActive={false} />
              </AreaChart>
            </ResponsiveContainer>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-6">
            {[
              { label: "24h High", value: `$${fmtPrice(coin.high_24h)}` },
              { label: "24h Low", value: `$${fmtPrice(coin.low_24h)}` },
              { label: "24h Volume", value: `${fmtPrice(coin.volume_24h)} ${coin.symbol}` },
              { label: "24h Volume (USD)", value: `$${coin.volume_24h_usd >= 1e9 ? (coin.volume_24h_usd / 1e9).toFixed(2) + "B" : (coin.volume_24h_usd / 1e6).toFixed(1) + "M"}` },
            ].map((s) => (
              <div key={s.label} className="bg-[#121212] border border-white/10 rounded-xl p-4">
                <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">{s.label}</p>
                <p className="font-mono text-lg text-white mt-1 font-semibold">{s.value}</p>
              </div>
            ))}
          </div>

          {/* Coin → Protocol bridge: verified registry relationship (token_of)
              only — never inferred. Live protocol → Entity Page; non-live →
              informational identity, no fake CTA (Rule 6). */}
          {assocProtocol && (
            <div className="mt-6 bg-[#121212] border border-white/10 rounded-xl p-4 sm:p-5"
              data-testid="coin-protocol-bridge">
              <p className="font-mono text-[10px] uppercase tracking-widest text-gray-500">Associated Protocol</p>
              {assocRoute ? (
                <Link to={assocRoute} data-testid="coin-protocol-bridge-link"
                  aria-label={`${assocProtocol.name} — Entity Intelligence`}
                  onClick={() => entityTrack("entity_nav_click", assocProtocol.id, { surface: "coin_protocol_bridge" })}
                  className="group mt-2.5 inline-flex items-center gap-2.5 rounded-lg focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-[#00E5FF]/60">
                  <EntityLogo type="protocol" slug={assocProtocol.slug} name={assocProtocol.name} size={28} />
                  <span className="font-bold text-[15px] text-white transition-colors duration-200 group-hover:text-[#00E5FF]">{assocProtocol.name}</span>
                  <span className="rounded border border-[#00E5FF]/40 bg-[#00E5FF]/10 px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-[0.16em] text-[#00E5FF]">PROTOCOL</span>
                  <ArrowUpRight aria-hidden
                    className="h-3.5 w-3.5 shrink-0 text-[#00E5FF] opacity-60 md:opacity-0 md:group-hover:opacity-100 transition-opacity duration-200" />
                </Link>
              ) : (
                <span className="mt-2.5 inline-flex items-center gap-2.5" data-testid="coin-protocol-bridge-inert" data-live="false">
                  <EntityLogo type="protocol" slug={assocProtocol.slug} name={assocProtocol.name} size={28} />
                  <span className="font-bold text-[15px] text-white">{assocProtocol.name}</span>
                  <span className="rounded border border-white/20 bg-white/[0.05] px-1.5 py-0.5 font-mono text-[8px] font-bold tracking-[0.16em] text-gray-400">PROTOCOL</span>
                </span>
              )}
              <p className="font-mono text-[8.5px] uppercase tracking-wider text-gray-500 mt-2.5">
                Verified registry relationship · the token is a distinct entity from the protocol
              </p>
            </div>
          )}
        </>
      )}

      <div className="mt-14" data-testid="coin-news-section">
        <h2 className="font-heading font-bold text-white text-xl sm:text-2xl mb-6">
          Latest {coin?.name || symbol?.toUpperCase()} news
        </h2>
        {(news?.items || []).length > 0 ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {news.items.map((a: Article) => <ArticleCard key={a.id} article={a} />)}
          </div>
        ) : (
          <p className="text-gray-500 py-10 text-center border border-dashed border-white/10 rounded-xl">
            No stories yet — the newsroom is watching this one.
          </p>
        )}
      </div>
    </div>
  );
}
